import React, { useMemo, useState } from "react";
import { Camera, ExternalLink, Loader2 } from "lucide-react";
import { wikiAttachmentUrl } from "@/lib/api";
import { germanDate, WIKI_CATEGORIES } from "@/lib/format";

const monthKey = (iso) => (iso || "").slice(0, 7);
const monthLabel = (iso) => {
    try {
        return new Intl.DateTimeFormat("de-DE", { month: "long", year: "numeric" }).format(new Date(iso));
    } catch {
        return iso;
    }
};
const catLabel = (v) => WIKI_CATEGORIES.find((c) => c.value === v)?.label || null;

/**
 * Chronologische Bilderstrecke aller Wiki-Fotos einer Immobilie.
 *
 * Reine Darstellung — die Wiki-Einträge kommen von der Immobilien-Detailseite,
 * die sie ohnehin lädt. `entries === null` bedeutet "lädt noch".
 */
export default function PhotoTimeline({ entries, error = null, onOpenEntry }) {
    const [preview, setPreview] = useState(null);

    const photos = useMemo(() => {
        const out = [];
        (entries || []).forEach((e) => {
            (e.attachments || []).forEach((a) => {
                if ((a.content_type || "").startsWith("image/")) {
                    out.push({ ...a, entryId: e.id, entryTitle: e.title, category: e.category });
                }
            });
        });
        out.sort((x, y) => (y.created_at || "").localeCompare(x.created_at || ""));
        return out;
    }, [entries]);

    const groups = useMemo(() => {
        const g = [];
        photos.forEach((ph) => {
            const key = monthKey(ph.created_at);
            let grp = g[g.length - 1];
            if (!grp || grp.key !== key) {
                grp = { key, label: monthLabel(ph.created_at), photos: [] };
                g.push(grp);
            }
            grp.photos.push(ph);
        });
        return g;
    }, [photos]);

    if (error) {
        return <div className="p-10 text-center text-rose-600 text-sm" data-testid="photo-timeline-error">Fehler beim Laden: {error}</div>;
    }

    if (entries === null) {
        return (
            <div className="p-10 text-center text-gray-400 text-sm inline-flex items-center gap-2 w-full justify-center">
                <Loader2 size={14} className="animate-spin" /> Lade Fotos…
            </div>
        );
    }

    if (photos.length === 0) {
        return (
            <div className="bg-white border border-gray-200 rounded-md p-12 text-center" data-testid="photo-timeline-empty">
                <Camera size={28} className="mx-auto text-gray-300 mb-3" />
                <div className="text-sm text-gray-500 font-medium">Noch keine Fotos zu diesem Objekt</div>
                <div className="text-xs text-gray-400 mt-1 max-w-md mx-auto">
                    Fotos erscheinen hier automatisch, sobald sie an Wiki-Einträge dieser Immobilie angehängt werden —
                    im Web über »Datei anhängen« oder per Telegram-Bot.
                </div>
            </div>
        );
    }

    return (
        <div data-testid="photo-timeline">
            <div className="text-xs text-gray-500 mb-4">
                {photos.length} {photos.length === 1 ? "Foto" : "Fotos"} aus dem Objekt-Wiki, chronologisch (neueste zuerst).
            </div>
            <div className="relative pl-7 space-y-8">
                <div className="absolute left-[7px] top-2 bottom-2 w-px bg-gray-200" />
                {groups.map((g) => (
                    <div key={g.key} data-testid={`photo-month-${g.key}`}>
                        <div className="flex items-center gap-2.5 mb-3 -ml-7">
                            <span className="w-[15px] h-[15px] rounded-full bg-slate-900 border-[3px] border-white shadow shrink-0" />
                            <span className="font-heading font-semibold text-slate-900 text-sm">{g.label}</span>
                            <span className="text-[10px] bg-gray-100 text-gray-500 rounded-full px-1.5 py-0.5 tabular-nums">{g.photos.length}</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            {g.photos.map((ph) => (
                                <div key={ph.id} className="group bg-white border border-gray-200 rounded-md overflow-hidden hover:shadow-md transition-shadow" data-testid={`timeline-photo-${ph.id}`}>
                                    <button
                                        className="block w-full aspect-[4/3] bg-gray-50"
                                        onClick={() => setPreview(ph)}
                                        title={ph.filename}
                                        data-testid={`timeline-photo-open-${ph.id}`}
                                    >
                                        <img
                                            src={wikiAttachmentUrl(ph.id)}
                                            alt={ph.filename}
                                            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
                                            loading="lazy"
                                        />
                                    </button>
                                    <div className="px-3 py-2">
                                        <div className="text-xs font-medium text-slate-900 truncate" title={ph.entryTitle}>{ph.entryTitle}</div>
                                        <div className="flex items-center justify-between mt-0.5">
                                            <span className="text-[10px] text-gray-400 tabular-nums">{germanDate(ph.created_at)}</span>
                                            <button
                                                className="text-[10px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-0.5"
                                                onClick={() => onOpenEntry && onOpenEntry(ph.entryId)}
                                                data-testid={`timeline-photo-entry-${ph.id}`}
                                            >
                                                Zum Eintrag <ExternalLink size={9} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {preview && (
                <div
                    className="fixed inset-0 bg-black/85 z-50 flex flex-col items-center justify-center p-4 gap-3"
                    onClick={() => setPreview(null)}
                    data-testid="timeline-lightbox"
                >
                    <img src={wikiAttachmentUrl(preview.id)} alt={preview.filename} className="max-h-[82vh] max-w-[92vw] object-contain rounded" />
                    <div className="text-center text-white/90 text-sm">
                        <div className="font-medium">{preview.entryTitle}</div>
                        <div className="text-xs text-white/60 mt-0.5">
                            {germanDate(preview.created_at)}{catLabel(preview.category) ? ` · ${catLabel(preview.category)}` : ""} · {preview.filename}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
